import React, { memo } from 'react';
import classNames, { default as classnames } from 'classnames';

import { ScommesseAvvenimentoScore } from 'components/scommesseAvvenimentoScore';
import { ScommesseTableCompetitors } from 'components/scommesseTable/scommesseTableCompetitors';
import { ScommesseTableDateTime } from 'components/scommesseTable/scommesseTableDateTime';
import { ScommesseTableStats } from 'components/scommesseTable/scommesseTableStats';
import { ScommesseTableWidget } from 'components/scommesseTable/scommesseTableWidget';
import { SportsCompetitor } from 'types/swagger';
import styles from './ScommesseTableAvvenimento.module.scss';
import { useAvvenimento, useAvvenimentoLink, useLiveInfo } from 'features/sport/hooks';
import { useTypedSelector } from 'lib/centralStore';
import { getDateAndHoursValue } from 'features/sport';
import { useAmbienteContext } from 'context/Ambiente';
import { selectElapsedTime } from 'lib/selectors';
import { shallowEqual } from 'fast-equals';
import useWindowSize from 'hooks/useWindowSize';
import { AnchorLink } from 'components/anchorLink';

export type ScommesseTableAvvenimentoProps = {
  // data?: string;
  // ora: string;
  live?: boolean;
  widget?: {
    id: string;
    url?: string;
    action?: () => void;
  }[];
  competitors: Array<SportsCompetitor>;
  urlAvvenimento: string;
  providerID?: number;
  className?: string;
  hasBorderSide?: boolean;
  hasBorderTop?: boolean;
  disableWidget?: boolean;
  hasMultipleLines?: boolean;
  avvenimentoKey?: string;
  // time?: {
  //   minutes: number;
  //   seconds: number;
  //   elapsedTime: number | undefined;
  // };
  results?: number;
  showOnlyHours?: boolean;
};

const emptyElapsed = { elapsedTime: null, minutes: null, seconds: null };

const ScommesseTableDateTimeMemo = memo(function ScommesseTableAvvenimento({
  hasBorderSide,
  showOnlyHours,
  avvenimentoKey,
}: Partial<ScommesseTableAvvenimentoProps>) {
  const { server, score, status, matchTime } = useLiveInfo(avvenimentoKey) ?? {};
  const avvenimento = useAvvenimento(avvenimentoKey);
  const [date, hours] = getDateAndHoursValue(avvenimento?.dataOra);

  const { pathScommessa } = useAmbienteContext();
  const { elapsedTime, minutes, seconds } = useTypedSelector(
    (state) =>
      pathScommessa ? selectElapsedTime(state?.[pathScommessa?.[0]]?.currentTime, avvenimento?.dataOra!) : emptyElapsed,
    shallowEqual
  );

  return (
    <React.Fragment>
      {(server || score) && <ScommesseAvvenimentoScore server={server} score={score} className={styles.score} />}
      <ScommesseTableDateTime
        key={`${status}-${matchTime}`}
        ora={hours}
        data={!showOnlyHours ? date : undefined}
        className={classNames(styles.dateTime, { [styles.borderLeft]: hasBorderSide })}
        avvenimentoKey={avvenimentoKey}
        time={elapsedTime ? { minutes, seconds, elapsedTime } : undefined}
      />
    </React.Fragment>
  );
});

export const ScommesseTableAvvenimento = memo(function ScommesseTableAvvenimento({
  live,
  widget,
  results,
  className,
  providerID,
  competitors,
  hasBorderSide,
  disableWidget,
  showOnlyHours,
  avvenimentoKey,
  hasMultipleLines,
}: ScommesseTableAvvenimentoProps) {
  const { server, score, redCards, status, matchTime } = useLiveInfo(avvenimentoKey) ?? {};
  const hasStats = !live && (providerID ?? 0) > 0;
  const { isMobile } = useWindowSize();
  const { found, longUrl, shortUrl, first } = useAvvenimentoLink(avvenimentoKey);
  return (
    <div
      className={classnames(className, styles.container, {
        [styles.borderTop]: disableWidget,
        [styles.borderRight]: disableWidget,
      })}
    >
      <ScommesseTableDateTimeMemo
        key={`${status}-${matchTime}`}
        className={classNames(styles.dateTime, { [styles.borderLeft]: hasBorderSide })}
        showOnlyHours={showOnlyHours}
        avvenimentoKey={avvenimentoKey}
      />

      {!!results && results > 0 && (
        <div className={styles.results}>
          <AnchorLink href={first ? shortUrl : longUrl} className={styles.resultsText}>
            {`[+${results}]`}
          </AnchorLink>
        </div>
      )}

      {widget && <ScommesseTableWidget items={widget} className={styles.widget} />}
      <ScommesseTableCompetitors
        redCards={redCards}
        className={styles.competitors}
        competitors={competitors}
        isFullWidth={hasMultipleLines}
        avvenimentoKey={avvenimentoKey}
      />
      {hasStats && <ScommesseTableStats providerID={providerID} className={styles.stats} />}
    </div>
  );
});